import React, { useEffect, useState } from 'react';
import {
  Loader2, ShieldCheck, PlusCircle, Edit3, RefreshCw, MinusCircle, X, AlertTriangle,
} from 'lucide-react';
import { dynamicSupplyDiff } from '../api';

const numFmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));

/**
 * ImportDiffPreview — safe confirmation step BEFORE committing an import.
 * Shows exactly which supply rows will be created, quantity-updated,
 * status-updated, or wiped (replace_supplier mode). No writes happen here.
 */
export default function ImportDiffPreview({
  token, supplier, mode, mapping,
  onConfirm, onCancel, committing,
}) {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    dynamicSupplyDiff({ token, supplier, mode, mapping })
      .then((d) => { if (alive) setDiff(d); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, supplier, mode, mapping]);

  if (loading) {
    return (
      <div className="se-panel p-8 text-center" data-testid="diff-loading">
        <Loader2 size={26} className="animate-spin mx-auto mb-2" style={{ color: 'var(--se-emerald)' }} />
        <div className="text-slate-600 text-[13px]">جاري حساب التغييرات...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="se-panel p-6" data-testid="diff-error" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <div className="text-red-900 font-bold mb-1">تعذر حساب التغييرات</div>
            <div className="text-red-800 text-[12px]">{error}</div>
            <button onClick={onCancel} className="se-btn se-btn-ghost mt-3">إلغاء</button>
          </div>
        </div>
      </div>
    );
  }
  if (!diff) return null;

  const c = diff.counts;
  const totalChanges = c.insert + c.update_qty + c.update_status;
  const zeroChanges = totalChanges === 0 && c.no_change > 0 && c.replace_delete === 0;
  const bigChange = c.replace_delete > 0 || (c.insert + c.update_qty) > 1000;

  const kpis = [
    { key: 'insert',        label: 'سجلات جديدة',       value: c.insert,        icon: PlusCircle, tint: '#16A34A' },
    { key: 'update_qty',    label: 'تحديث كمية',        value: c.update_qty,    icon: Edit3,      tint: '#D97706' },
    { key: 'update_status', label: 'تحديث حالة',        value: c.update_status, icon: RefreshCw,  tint: '#0F766E' },
    { key: 'no_change',     label: 'بدون تغيير',        value: c.no_change,     icon: ShieldCheck, tint: '#94A3B8' },
  ];
  if (mode === 'replace_supplier' && c.replace_delete > 0) {
    kpis.unshift({ key: 'delete', label: 'سيتم حذفها', value: c.replace_delete, icon: MinusCircle, tint: '#DC2626' });
  }

  return (
    <div className="se-panel p-5" data-testid="diff-preview">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[11px] font-semibold" style={{ color: 'var(--se-emerald)' }}>DIFF PREVIEW</div>
          <div className="text-[16px] font-bold mt-1 text-[color:var(--se-ink)]">معاينة التغييرات قبل الحفظ</div>
          <div className="text-slate-500 text-[12px] mt-1">
            سيتم تطبيق {numFmt(diff.total_records)} سجل ·
            الموردون: <span className="font-semibold text-slate-700">{diff.suppliers.join('، ') || '—'}</span> ·
            الوضع: <span className="font-semibold text-slate-700">{mode === 'merge' ? 'دمج' : 'استبدال المورد'}</span>
          </div>
        </div>
        <button onClick={onCancel} className="se-btn se-btn-ghost" data-testid="diff-cancel">
          <X size={13} /> إلغاء
        </button>
      </div>

      {/* KPI tiles */}
      <div className={`grid gap-3 mb-5 ${mode === 'replace_supplier' && c.replace_delete > 0 ? 'grid-cols-5' : 'grid-cols-4'}`}>
        {kpis.map((k) => (
          <div key={k.key} className="se-panel p-3" data-testid={`diff-kpi-${k.key}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10.5px] text-slate-500 font-medium">{k.label}</div>
              <k.icon size={14} style={{ color: k.tint }} />
            </div>
            <div className="text-[22px] font-extrabold tabular-nums" style={{ color: k.tint }}>{numFmt(k.value)}</div>
          </div>
        ))}
      </div>

      {/* Workbook Reconciliation — SOURCE vs CALCULATED with status banner */}
      {diff.workbook_reconciliation && (
        <WorkbookReconciliationBanner recon={diff.workbook_reconciliation} />
      )}

      {/* §17: Execution + Financial validation BEFORE commit */}
      {diff.execution_summary && (
        <ExecutionSummaryPanel
          summary={diff.execution_summary}
          workbookMetrics={diff.workbook_summary_metrics}
        />
      )}

      {/* Import Reconciliation summary — validation totals BEFORE commit */}
      {diff.reconciliation && (
        <div className="se-panel p-4 mb-5" data-testid="diff-reconciliation" style={{ background: '#F8FAFF', border: '1px solid #C7D2FE' }}>
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={15} style={{ color: '#4F46E5' }} />
            <div className="text-[13px] font-bold text-indigo-900">مطابقة الاستيراد — قبل الحفظ</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ReconTile label="السجلات" value={numFmt(diff.reconciliation.total_records)} tint="#4F46E5" />
            <ReconTile label="الكمية الإجمالية" value={numFmt(diff.reconciliation.total_quantity)} tint="#0F766E" />
            <ReconTile label="القيمة (ر.س)" value={numFmt(Math.round(diff.reconciliation.total_value))} tint="#16A34A" />
            <ReconTile label="مدارس فريدة" value={numFmt(diff.reconciliation.unique_schools)} tint="#7C3AED" />
          </div>
          {(diff.reconciliation.warnings?.schools_missing_ministerial > 0 ||
            diff.reconciliation.warnings?.rows_missing_price > 0 ||
            diff.reconciliation.warnings?.rows_missing_quantity > 0) && (
            <div className="mt-3 flex items-center flex-wrap gap-3 text-[11.5px]">
              {diff.reconciliation.warnings.schools_missing_ministerial > 0 && (
                <span className="px-2 py-1 rounded" style={{ background: '#FEF3C7', color: '#92400E' }}>
                  {numFmt(diff.reconciliation.warnings.schools_missing_ministerial)} مدرسة بلا رقم وزاري (تم توليد معرف داخلي)
                </span>
              )}
              {diff.reconciliation.warnings.rows_missing_price > 0 && (
                <span className="px-2 py-1 rounded" style={{ background: '#FEE2E2', color: '#991B1B' }}>
                  {numFmt(diff.reconciliation.warnings.rows_missing_price)} صف بدون سعر
                </span>
              )}
              {diff.reconciliation.warnings.rows_missing_quantity > 0 && (
                <span className="px-2 py-1 rounded" style={{ background: '#FEE2E2', color: '#991B1B' }}>
                  {numFmt(diff.reconciliation.warnings.rows_missing_quantity)} صف بدون كمية
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Highlighted warning for big changes */}
      {bigChange && (
        <div className="p-3 rounded-lg mb-4" style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }} data-testid="diff-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-amber-900 text-[12.5px]">
              {c.replace_delete > 0
                ? <>سيتم حذف <b>{numFmt(c.replace_delete)}</b> سجل موجود ثم إعادة تعبئتها من هذا الملف. تأكد أنك لا تريد الدمج بدلاً من ذلك.</>
                : <>عدد كبير من التغييرات ({numFmt(c.insert + c.update_qty)}). راجع العينة أدناه قبل التأكيد.</>}
            </div>
          </div>
        </div>
      )}

      {zeroChanges && (
        <div className="p-3 rounded-lg mb-4" style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }}>
          <div className="text-emerald-800 text-[12.5px] flex items-center gap-2">
            <ShieldCheck size={14} /> جميع البيانات مطابقة لما هو موجود — لن يحدث أي تغيير فعلي.
          </div>
        </div>
      )}

      {/* Sample changes table */}
      {diff.sample_changes.length > 0 && (
        <div className="mb-5">
          <div className="text-slate-700 text-[13px] font-bold mb-2">عيّنة التغييرات ({diff.sample_changes.length} من {numFmt(totalChanges)})</div>
          <div className="se-panel overflow-x-auto max-h-[380px] overflow-y-auto">
            <table className="se-table" data-testid="diff-sample-table">
              <thead className="sticky top-0" style={{ zIndex: 1 }}>
                <tr>
                  <th>النوع</th>
                  <th>المدرسة</th>
                  <th>الصنف</th>
                  <th className="text-center">الكمية السابقة</th>
                  <th className="text-center">الكمية الجديدة</th>
                  <th className="text-center">الحالة السابقة</th>
                  <th className="text-center">الحالة الجديدة</th>
                </tr>
              </thead>
              <tbody>
                {diff.sample_changes.map((r, i) => (
                  <tr key={i} data-testid={`diff-row-${i}`}>
                    <td><TypePill type={r.type} /></td>
                    <td className="truncate max-w-[220px]" title={r.school_name || r.school_id}>
                      <div className="font-semibold text-[12px] text-[color:var(--se-ink)]">{r.school_name || r.school_id || '—'}</div>
                      {r.school_id && r.school_name && <div className="text-slate-500 text-[10.5px]">{r.school_id}</div>}
                    </td>
                    <td className="truncate max-w-[240px]" title={r.item}>{r.item}</td>
                    <td className="text-center tabular-nums text-slate-500">{r.old_quantity == null ? '—' : numFmt(r.old_quantity)}</td>
                    <td className="text-center tabular-nums font-bold">{numFmt(r.new_quantity)}</td>
                    <td className="text-center text-slate-500 text-[11px]">{r.old_status || '—'}</td>
                    <td className="text-center text-[11px]">{r.new_status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: 'var(--se-border)' }}>
        <div className="text-slate-500 text-[11.5px]">
          {c.new_schools > 0 && <span>مدارس جديدة: <b className="text-slate-800">{numFmt(c.new_schools)}</b></span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="se-btn se-btn-ghost" data-testid="diff-back">رجوع</button>
          <button onClick={onConfirm}
            disabled={committing || zeroChanges}
            className="se-btn se-btn-primary disabled:opacity-40"
            data-testid="diff-confirm">
            {committing ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
            {zeroChanges ? 'لا شيء للحفظ' : `تأكيد وحفظ ${numFmt(totalChanges)} تغيير`}
          </button>
        </div>
      </div>
    </div>
  );
}

function TypePill({ type }) {
  const map = {
    insert:                 { txt: 'جديد',         cls: 'se-pill se-pill-green' },
    update_qty:             { txt: 'تحديث كمية',   cls: 'se-pill se-pill-amber' },
    update_qty_and_status:  { txt: 'كمية + حالة',  cls: 'se-pill se-pill-amber' },
    update_status:          { txt: 'تحديث حالة',   cls: 'se-pill se-pill-emerald' },
    no_change:              { txt: 'بلا تغيير',    cls: 'se-pill' },
  };
  const p = map[type] || map.update_qty;
  return <span className={p.cls}>{p.txt}</span>;
}

function ReconTile({ label, value, tint }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: tint }}>{label}</div>
      <div className="text-[20px] font-extrabold tabular-nums mt-0.5" style={{ color: '#0F172A' }}>{value}</div>
    </div>
  );
}

function WorkbookReconciliationBanner({ recon }) {
  const status = recon.status;
  const isRVN = recon.kind === 'raw_vs_normalized';
  const palette = {
    MATCHED:  { bg: '#F0FDF4', border: '#86EFAC', text: '#166534', label: 'مطابق' },
    WARNING:  { bg: '#FFFBEB', border: '#FCD34D', text: '#92400E', label: 'تحذير' },
    MISMATCH: { bg: '#FEF2F2', border: '#FCA5A5', text: '#991B1B', label: 'اختلاف' },
    ERROR:    { bg: '#FEE2E2', border: '#EF4444', text: '#7F1D1D', label: 'خطأ' },
  }[status] || { bg: '#F1F5F9', border: '#CBD5E1', text: '#334155', label: status };
  const [showItems, setShowItems] = React.useState(false);
  // For raw-vs-normalized any non-zero diff is a failure (parser is
  // deterministic — no rounding wiggle needed beyond 0.01).
  const perItem = recon.per_item || [];
  const mismatched = isRVN
    ? perItem.filter((r) => r.ok === false)
    : perItem.filter((r) =>
        (r.qty_diff != null && Math.abs(r.qty_diff) > 1) ||
        (r.value_diff != null && Math.abs(r.value_diff) > 1)
      );
  // Unified accessors that read either the new RVN shape or the old
  // (source/calculated) shape without breaking either.
  const leftQty   = isRVN ? recon.raw?.quantity        : recon.calculated?.quantity;
  const rightQty  = isRVN ? recon.normalized?.quantity : recon.source?.quantity;
  const leftVal   = isRVN ? recon.raw?.value           : recon.calculated?.value;
  const rightVal  = isRVN ? recon.normalized?.value    : recon.source?.value;
  const leftLbl   = isRVN ? 'خام (Excel)' : 'محسوب';
  const rightLbl  = isRVN ? 'مطبّع (سيتم حفظه)' : 'مصدر';
  return (
    <div className="p-4 rounded-lg mb-5" data-testid="workbook-reconciliation"
         style={{ background: palette.bg, border: `1px solid ${palette.border}` }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} style={{ color: palette.text }} />
          <div className="text-[13px] font-bold" style={{ color: palette.text }}>
            {isRVN
              ? 'مطابقة الخام (Excel) مع السجلات المطبّعة'
              : 'مطابقة الملف مع الحساب التفصيلي'} — <span className="uppercase tracking-widest">{palette.label}</span>
          </div>
        </div>
        {mismatched.length > 0 && (
          <button data-testid="toggle-per-item" onClick={() => setShowItems(!showItems)}
                  className="text-[11.5px] font-semibold" style={{ color: palette.text }}>
            {showItems ? 'إخفاء بنود الاختلاف' : `عرض ${mismatched.length} بند يحمل اختلافاً`}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: palette.text }}>الكمية</div>
          <div className="text-[13px] mt-1" style={{ color: '#0F172A' }}>
            {leftLbl}: <span className="font-bold tabular-nums">{fmt(leftQty)}</span>
          </div>
          <div className="text-[13px]" style={{ color: '#0F172A' }}>
            {rightLbl}: <span className="font-bold tabular-nums">{fmt(rightQty)}</span>
          </div>
          <div className="text-[12.5px] mt-1 font-bold" style={{ color: palette.text }}>
            فرق: {fmtDiff(recon.difference?.quantity)}
          </div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: palette.text }}>القيمة (ر.س)</div>
          <div className="text-[13px] mt-1" style={{ color: '#0F172A' }}>
            {leftLbl}: <span className="font-bold tabular-nums">{fmt(leftVal)}</span>
          </div>
          <div className="text-[13px]" style={{ color: '#0F172A' }}>
            {rightLbl}: <span className="font-bold tabular-nums">{fmt(rightVal)}</span>
          </div>
          <div className="text-[12.5px] mt-1 font-bold" style={{ color: palette.text }}>
            فرق: {fmtDiff(recon.difference?.value)}
          </div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: palette.text }}>الحالة</div>
          <div className="text-[13px] mt-1" style={{ color: '#0F172A' }}>
            {status === 'MATCHED' && (isRVN ? 'الإجماليات الخام تطابق السجلات التي ستُحفظ.' : 'إجماليات الملف تطابق الحساب التفصيلي.')}
            {status === 'WARNING' && 'لا يحتوي الملف على إجمالي مرجعي كامل — قد تكون البيانات صحيحة لكن غير قابلة للتحقق.'}
            {status === 'MISMATCH' && (isRVN
              ? 'خام Excel لا يطابق السجلات المطبّعة. الاستيراد متوقف — راجع البنود المختلفة أدناه.'
              : 'الإجماليات لا تطابق التفاصيل.')}
            {status === 'ERROR' && 'الملف لا يمكن تحليله بأمان.'}
          </div>
        </div>
      </div>
      {showItems && mismatched.length > 0 && (
        <div className="mt-3 overflow-x-auto" data-testid="per-item-reconciliation">
          <table className="se-table text-[11px]">
            <thead><tr>
              <th>الصنف</th>
              <th className="text-center">{isRVN ? 'كمية خام' : 'الكمية (مصدر)'}</th>
              <th className="text-center">{isRVN ? 'كمية مطبّعة' : 'الكمية (محسوب)'}</th>
              <th className="text-center">فرق الكمية</th>
              <th className="text-center">{isRVN ? 'قيمة خام' : 'القيمة (مصدر)'}</th>
              <th className="text-center">{isRVN ? 'قيمة مطبّعة' : 'القيمة (محسوب)'}</th>
              <th className="text-center">فرق القيمة</th>
            </tr></thead>
            <tbody>
              {mismatched.slice(0, 40).map((r, i) => (
                <tr key={i}>
                  <td className="truncate max-w-[240px]" title={r.item}>{r.item}</td>
                  <td className="text-center tabular-nums">{fmt(isRVN ? r.raw_qty : r.source_qty)}</td>
                  <td className="text-center tabular-nums">{fmt(isRVN ? r.norm_qty : r.calculated_qty)}</td>
                  <td className="text-center tabular-nums font-bold" style={{ color: palette.text }}>{fmtDiff(r.qty_diff)}</td>
                  <td className="text-center tabular-nums">{fmt(isRVN ? r.raw_value : r.source_value)}</td>
                  <td className="text-center tabular-nums">{fmt(isRVN ? r.norm_value : r.calculated_value)}</td>
                  <td className="text-center tabular-nums font-bold" style={{ color: palette.text }}>{fmtDiff(r.value_diff)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmt(n) { return n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }); }
function fmtDiff(n) {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

/**
 * ExecutionSummaryPanel — §17 of the FINAL PRODUCTION FIX.
 * Displays row-level physical (delivery) + financial execution
 * validation BEFORE commit.  All fields are optional so the panel
 * gracefully degrades when the workbook doesn't carry that data —
 * per §15 "field genuinely missing = display غير متوفر", but never
 * silently show 0.
 */
function ExecutionSummaryPanel({ summary, workbookMetrics }) {
  if (!summary || summary.error) return null;
  const hasPhysical  = summary.has_supply_status_column;
  const hasFinancial = summary.has_row_financial_column;
  if (!hasPhysical && !hasFinancial) return null;

  const wm = workbookMetrics || {};
  const srcFinTotal = wm.source_total_financial_completion;
  const srcCompletionPct = wm.source_workbook_completion_pct;

  const finStatus = summary.financial_validation_status;
  const finStatusPalette = {
    MATCHED:  { bg: '#F0FDF4', border: '#86EFAC', text: '#166534', label: 'مطابق' },
    MISMATCH: { bg: '#FEF2F2', border: '#FCA5A5', text: '#991B1B', label: 'اختلاف' },
    NOT_APPLICABLE: { bg: '#F1F5F9', border: '#CBD5E1', text: '#475569', label: 'غير قابل للتحقق' },
  }[finStatus] || { bg: '#F1F5F9', border: '#CBD5E1', text: '#475569', label: finStatus };

  return (
    <div className="se-panel p-4 mb-5" data-testid="execution-summary"
         style={{ background: '#FAF7FF', border: '1px solid #E9D5FF' }}>
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={15} style={{ color: '#7C3AED' }} />
        <div className="text-[13px] font-bold" style={{ color: '#5B21B6' }}>
          التنفيذ والإنجاز المالي — قبل الحفظ
        </div>
      </div>

      {/* Physical execution row */}
      {hasPhysical ? (
        <div className="mb-4">
          <div className="text-[10.5px] uppercase tracking-widest font-semibold mb-2" style={{ color: '#5B21B6' }}>
            التنفيذ الفعلي
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <ExecTile label="إجمالي الكمية المخصصة" value={fmt(summary.total_allocated_quantity)} tint="#7C3AED" testid="es-alloc-qty" />
            <ExecTile label="الكمية المنفذة" value={fmt(summary.delivered_quantity)} tint="#0F766E" testid="es-del-qty" />
            <ExecTile label="الكمية المتبقية" value={fmt(summary.remaining_quantity)} tint="#B45309" testid="es-remaining-qty" />
            <ExecTile label="عدد المدارس المنفذ لها" value={fmt(summary.delivered_school_count)} tint="#4F46E5" testid="es-del-schools" />
            <ExecTile label="نسبة إنجاز التنفيذ" value={summary.physical_completion_pct == null ? '—' : `${Number(summary.physical_completion_pct).toFixed(4)}%`} tint="#16A34A" testid="es-phys-pct" />
          </div>
        </div>
      ) : (
        <div className="mb-4 text-[12px] text-slate-500 flex items-center gap-2">
          <AlertTriangle size={13} /> عمود «حالة التوريد» غير موجود في هذا الملف — لا يمكن حساب التنفيذ الفعلي.
        </div>
      )}

      {/* Financial execution row */}
      {hasFinancial ? (
        <div>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold mb-2" style={{ color: '#5B21B6' }}>
            الإنجاز المالي
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <ExecTile label="القيمة المنجزة (ر.س)" value={fmt(summary.row_financial_completed_value)} tint="#16A34A" testid="es-fin-value" />
            <ExecTile label="نسبة الإنجاز المالي المحسوبة" value={summary.calculated_financial_completion_pct == null ? '—' : `${Number(summary.calculated_financial_completion_pct).toFixed(4)}%`} tint="#7C3AED" testid="es-calc-fin-pct" />
            <ExecTile label="القيمة من الأصناف المنفذة" value={fmt(summary.delivered_value_from_items)} tint="#0F766E" testid="es-del-value-items" />
            <ExecTile label={<>مجموع الإنجاز المالي<br/><span className="text-[9px] text-slate-500">من الملف</span></>} value={srcFinTotal == null ? 'غير متوفر' : fmt(srcFinTotal)} tint="#334155" testid="es-src-fin-total" />
            <ExecTile label={<>نسبة الإنجاز<br/><span className="text-[9px] text-slate-500">من الملف</span></>} value={srcCompletionPct == null ? 'غير متوفر' : `${(Number(srcCompletionPct) * 100).toFixed(4)}%`} tint="#334155" testid="es-src-completion-pct" />
          </div>

          {/* Financial validation status pill */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span
              className="px-2 py-1 rounded text-[11px] font-bold"
              style={{ background: finStatusPalette.bg, border: `1px solid ${finStatusPalette.border}`, color: finStatusPalette.text }}
              data-testid="es-fin-validation-status">
              التحقق المالي: {finStatusPalette.label}
            </span>
            {summary.financial_validation_diff != null && (
              <span className="text-[11.5px] text-slate-600">
                الفرق بين القيمة المالية للسجل والقيمة المحسوبة من الأصناف المنفذة:
                <b className="mx-1 tabular-nums" style={{ color: finStatusPalette.text }}>{fmtDiff(summary.financial_validation_diff)}</b>
              </span>
            )}
            {summary.financial_parse_warnings > 0 && (
              <span className="px-2 py-1 rounded text-[11px]" style={{ background: '#FEF3C7', color: '#92400E' }}>
                {fmt(summary.financial_parse_warnings)} صف يحتوي قيمة مالية غير قابلة للتحويل
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="text-[12px] text-slate-500 flex items-center gap-2">
          <AlertTriangle size={13} /> عمود «الإنجاز المالي للمدرسة» غير موجود في هذا الملف — لا يمكن حساب الإنجاز المالي على مستوى الصف.
        </div>
      )}
    </div>
  );
}

function ExecTile({ label, value, tint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-semibold" style={{ color: tint }}>{label}</div>
      <div className="text-[16px] font-extrabold tabular-nums mt-1" style={{ color: '#0F172A' }}>{value}</div>
    </div>
  );
}
